/**
 * UnifiedHarvestForm Component
 *
 * Unified dialog for recording harvests against both standard and legacy batches.
 * Features a dropdown with legacy batches (top), standard batches (bottom),
 * and a "+Create New Legacy Batch" option.
 *
 * Part of Harvest Recording Enhancement
 */

import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
  SelectGroup,
  SelectLabel
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { useHarvestMutations } from '@/hooks/useHarvest'
import { useHarvestBatches } from '@/hooks/useHarvestBatches'
import { useAllStrains } from '@/hooks/useStrains'
import { parseDateString, toLocalDateString } from '@/utils/dateUtils'
import {
  Scale,
  Calendar,
  User,
  MessageSquare,
  Leaf,
  Clock,
  Plus
} from 'lucide-react'

// Quality grade options
const QUALITY_GRADES = [
  { value: 'A+', label: 'A+ (Premium)', description: 'Perfect specimens, premium pricing' },
  { value: 'A', label: 'A (Standard)', description: 'Good quality, standard retail' },
  { value: 'B', label: 'B (Second)', description: 'Minor imperfections, discounted' },
  { value: 'reject', label: 'Reject', description: 'Not saleable, compost or samples' }
]

// Special value for creating new legacy batch
const CREATE_NEW_LEGACY = '__CREATE_NEW__'

export default function UnifiedHarvestForm({ isOpen, onClose, onSave }) {
  // State
  const [selectedBatchId, setSelectedBatchId] = useState('')
  const [selectedStrainId, setSelectedStrainId] = useState('')
  const [formData, setFormData] = useState({
    harvest_date: toLocalDateString(new Date()),
    weight_kg: '',
    quality_grade: 'A',
    harvested_by: '',
    notes: ''
  })
  const [markAsSpent, setMarkAsSpent] = useState(false)
  const [errors, setErrors] = useState({})

  // Hooks
  const {
    legacyBatches,
    standardBatches,
    loading: batchesLoading,
    refetch: refetchBatches
  } = useHarvestBatches()
  const { strains, loading: strainsLoading } = useAllStrains({ activeOnly: true })
  const { recordHarvest, recordLegacyHarvest, loading: mutationLoading } = useHarvestMutations()

  // Computed
  const isCreatingNewLegacy = selectedBatchId === CREATE_NEW_LEGACY
  const selectedBatch = useMemo(() => {
    if (isCreatingNewLegacy || !selectedBatchId) return null
    return [...legacyBatches, ...standardBatches].find(b => b.id === selectedBatchId)
  }, [selectedBatchId, legacyBatches, standardBatches, isCreatingNewLegacy])

  const isLegacyBatch = useMemo(() => {
    if (isCreatingNewLegacy) return true
    return selectedBatch?.is_legacy || false
  }, [selectedBatch, isCreatingNewLegacy])

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      setSelectedBatchId('')
      setSelectedStrainId('')
      setFormData({
        harvest_date: toLocalDateString(new Date()),
        weight_kg: '',
        quality_grade: 'A',
        harvested_by: '',
        notes: ''
      })
      setMarkAsSpent(false)
      setErrors({})
      refetchBatches()
    }
  }, [isOpen, refetchBatches])

  const validateForm = () => {
    const newErrors = {}

    // Batch validation
    if (!selectedBatchId) {
      newErrors.batch = 'Please select a batch or create new'
    }

    // Strain validation (only for new legacy)
    if (isCreatingNewLegacy && !selectedStrainId) {
      newErrors.strain = 'Strain is required for new legacy batch'
    }

    // Weight validation
    if (!formData.weight_kg || parseFloat(formData.weight_kg) <= 0) {
      newErrors.weight_kg = 'Weight must be greater than 0'
    }
    if (parseFloat(formData.weight_kg) > 50) {
      newErrors.weight_kg = 'Weight seems unusually high (max 50kg)'
    }

    // Date validation
    if (!formData.harvest_date) {
      newErrors.harvest_date = 'Harvest date is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      if (isCreatingNewLegacy || isLegacyBatch) {
        // Legacy harvest - create batch/container automatically
        const strainId = isCreatingNewLegacy
          ? selectedStrainId
          : selectedBatch?.strain?.id

        await recordLegacyHarvest({
          strain_id: strainId,
          harvest_date: formData.harvest_date,
          weight_kg: parseFloat(formData.weight_kg),
          quality_grade: formData.quality_grade,
          harvested_by: formData.harvested_by || undefined,
          notes: formData.notes || undefined,
          mark_as_spent: markAsSpent
        })
      } else {
        // Standard harvest - need to select a container from the batch
        // For standard batches, we use the existing record harvest flow
        // This should be handled by selecting a specific container
        // For now, throw an error - this needs the container selector
        setErrors({ submit: 'Standard batch harvest requires container selection. This feature is coming soon.' })
        return
      }

      if (onSave) {
        onSave()
      }
      onClose()
    } catch (err) {
      setErrors({ submit: err.message })
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    return parseDateString(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short'
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Record Harvest
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Batch Selector */}
          <div className="space-y-2">
            <Label htmlFor="batch">Batch</Label>
            <Select
              value={selectedBatchId}
              onValueChange={(value) => {
                setSelectedBatchId(value)
                if (errors.batch) setErrors(prev => ({ ...prev, batch: undefined }))
              }}
              disabled={batchesLoading}
            >
              <SelectTrigger
                id="batch"
                className={errors.batch ? 'border-red-500' : ''}
              >
                <SelectValue placeholder={batchesLoading ? 'Loading...' : 'Select batch...'} />
              </SelectTrigger>
              <SelectContent>
                {/* Legacy Batches */}
                {legacyBatches.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="flex items-center gap-2">
                      <Clock className="h-3 w-3" />
                      Legacy Batches
                    </SelectLabel>
                    {legacyBatches.map(batch => (
                      <SelectItem key={batch.id} value={batch.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{batch.batch_code}</span>
                          <Badge variant="secondary" className="text-xs">
                            {batch.strain?.code}
                          </Badge>
                          <span className="text-xs text-content-tertiary">
                            {formatDate(batch.inoculation_date)}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}

                {/* Create New Legacy Option */}
                <SelectItem value={CREATE_NEW_LEGACY}>
                  <div className="flex items-center gap-2 text-odin-blue">
                    <Plus className="h-4 w-4" />
                    <span>Create New Legacy Batch</span>
                  </div>
                </SelectItem>

                {/* Separator */}
                {standardBatches.length > 0 && <SelectSeparator />}

                {/* Standard Batches */}
                {standardBatches.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="flex items-center gap-2">
                      <Leaf className="h-3 w-3" />
                      Standard Batches
                    </SelectLabel>
                    {standardBatches.map(batch => (
                      <SelectItem key={batch.id} value={batch.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{batch.batch_code}</span>
                          <Badge variant="outline" className="text-xs">
                            {batch.strain?.code}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {batch.current_state?.replace('_', ' ')}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}

                {/* Empty state */}
                {legacyBatches.length === 0 && standardBatches.length === 0 && !batchesLoading && (
                  <div className="py-2 px-2 text-sm text-content-tertiary">
                    No batches available
                  </div>
                )}
              </SelectContent>
            </Select>
            {errors.batch && (
              <p className="text-sm text-red-500">{errors.batch}</p>
            )}
          </div>

          {/* Strain Selector (only for new legacy) */}
          {isCreatingNewLegacy && (
            <div className="space-y-2">
              <Label htmlFor="strain">Strain</Label>
              <Select
                value={selectedStrainId}
                onValueChange={(value) => {
                  setSelectedStrainId(value)
                  if (errors.strain) setErrors(prev => ({ ...prev, strain: undefined }))
                }}
                disabled={strainsLoading}
              >
                <SelectTrigger
                  id="strain"
                  className={errors.strain ? 'border-red-500' : ''}
                >
                  <SelectValue placeholder={strainsLoading ? 'Loading...' : 'Select strain...'} />
                </SelectTrigger>
                <SelectContent>
                  {strains.map(strain => (
                    <SelectItem key={strain.id} value={strain.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{strain.code}</span>
                        <span>{strain.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.strain && (
                <p className="text-sm text-red-500">{errors.strain}</p>
              )}
            </div>
          )}

          {/* Selected Batch Info */}
          {selectedBatch && (
            <div className="rounded-lg bg-surface-secondary p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-content-secondary">Strain:</span>
                <span className="font-medium">{selectedBatch.strain?.name}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-content-secondary">Blocks:</span>
                <span>{selectedBatch.total_blocks || 0}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-content-secondary">Total harvested:</span>
                <span>{(selectedBatch.total_harvest_kg || 0).toFixed(2)} kg</span>
              </div>
            </div>
          )}

          {/* Harvest Date */}
          <div className="space-y-2">
            <Label htmlFor="harvest_date" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Harvest Date
            </Label>
            <Input
              id="harvest_date"
              type="date"
              value={formData.harvest_date}
              onChange={(e) => handleInputChange('harvest_date', e.target.value)}
              className={errors.harvest_date ? 'border-red-500' : ''}
            />
            {errors.harvest_date && (
              <p className="text-sm text-red-500">{errors.harvest_date}</p>
            )}
          </div>

          {/* Weight */}
          <div className="space-y-2">
            <Label htmlFor="weight_kg" className="flex items-center gap-2">
              <Scale className="h-4 w-4" />
              Weight (kg)
            </Label>
            <Input
              id="weight_kg"
              type="number"
              step="0.01"
              min="0"
              max="50"
              placeholder="e.g. 0.85"
              value={formData.weight_kg}
              onChange={(e) => handleInputChange('weight_kg', e.target.value)}
              className={errors.weight_kg ? 'border-red-500' : ''}
            />
            {errors.weight_kg && (
              <p className="text-sm text-red-500">{errors.weight_kg}</p>
            )}
          </div>

          {/* Quality Grade */}
          <div className="space-y-2">
            <Label htmlFor="quality_grade">Quality Grade</Label>
            <Select
              value={formData.quality_grade}
              onValueChange={(value) => handleInputChange('quality_grade', value)}
            >
              <SelectTrigger id="quality_grade">
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
              <SelectContent>
                {QUALITY_GRADES.map(grade => (
                  <SelectItem key={grade.value} value={grade.value}>
                    {grade.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-content-tertiary">
              {QUALITY_GRADES.find(g => g.value === formData.quality_grade)?.description}
            </p>
          </div>

          {/* Harvested By */}
          <div className="space-y-2">
            <Label htmlFor="harvested_by" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Harvested By (optional)
            </Label>
            <Input
              id="harvested_by"
              placeholder="Name of harvester"
              value={formData.harvested_by}
              onChange={(e) => handleInputChange('harvested_by', e.target.value)}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Notes (optional)
            </Label>
            <Textarea
              id="notes"
              placeholder="Any observations about this harvest..."
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={2}
            />
          </div>

          {/* Mark as Spent (for legacy batches) */}
          {isLegacyBatch && (
            <div className="flex items-center space-x-2 rounded-lg bg-surface-secondary p-3">
              <Checkbox
                id="mark_as_spent"
                checked={markAsSpent}
                onCheckedChange={setMarkAsSpent}
              />
              <Label
                htmlFor="mark_as_spent"
                className="text-sm font-normal cursor-pointer"
              >
                Mark block as spent (no more flushes expected)
              </Label>
            </div>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <Alert variant="destructive">
              <AlertDescription>{errors.submit}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutationLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={mutationLoading}>
            {mutationLoading ? 'Recording...' : 'Record Harvest'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
